import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';
import Dialog from 'components/Dialog';
import { remove, concat, uniqBy, map } from 'lodash';
import { useForm, useTranslation } from 'hooks';
import DeleteDialogFooter from '../../DeleteDialogFooter';
import { GroupInformation } from 'components/pages/ResumePage/sections/WorkExperience/Editor/forms';
import { Form as FormComponent } from '../Editor/style';

const components = {
  Footer: DeleteDialogFooter
};

function GroupEditor({ syncErrors, syncing, listOfComp, availableCompaniesToGroup = [], onClose, onSaveGroup, onDelete }) {
  const { t, tr } = useTranslation();
  const didMountRef = useRef(false);
  const location = useLocation();

  // Only render if this is a modal route (background state exists)
  const isModal = location.state?.background;
  const isOpen = isModal && location.pathname.includes('/add-company-group');

  const groupID = useMemo(() => {
    const pathParts = location.pathname.split('/');
    const last = pathParts[pathParts.length - 1];
    return last && last !== 'add-company-group' ? last : null;
  }, [location.pathname]);

  remove(listOfComp?.companies, obj => obj.compId === '');
  remove(availableCompaniesToGroup, obj => obj.compId === '');

  const companiesDOM = useMemo(
    () =>
      map(listOfComp?.companies, comp => ({
        key: comp.compId,
        value: comp.companyName
      })),
    [listOfComp]
  );

  const finalCompanies = useMemo(() => {
    const merged = concat(listOfComp?.companies || [], availableCompaniesToGroup);
    return uniqBy(merged, v => v.compId);
  }, [listOfComp, availableCompaniesToGroup]);

  const availableCompaniesDOM = useMemo(
    () =>
      map(finalCompanies, comp => ({
        key: comp.compId,
        value: comp.companyName
      })),
    [finalCompanies]
  );

  // Mutate listOfComp with derived props (same as old)
  if (listOfComp) {
    listOfComp.companiesInGroup = groupID ? map(companiesDOM, 'key') : [];
    listOfComp.availableCompanies = finalCompanies;
  }

  const { getValue, useInput, handleAdd, handleRemove, handleReset, handleSubmit } = useForm({
    initialValues: listOfComp,
    validationSchema: 'companyGroupSchema',
    onSubmit: onSaveGroup
  });

  const handleClose = useCallback(() => {
    handleReset();
    onClose();
  }, [handleReset, onClose]);

  useEffect(() => {
    if (didMountRef.current) {
      if (!syncErrors && !syncing) {
        handleClose();
      }
    } else {
      didMountRef.current = true;
    }
  }, [handleClose, syncErrors, syncing]);

  const footerProps = useMemo(() => {
    if (groupID) {
      return {
        onDelete: () => onDelete(groupID)
      };
    }
  }, [groupID, onDelete]);

  if (!isModal) return null;

  return (
    <Dialog
    actionLabel={t('button.save')}
    components={components}
    disabled={syncing}
    footerProps={footerProps}
    open={isOpen}
    syncing={syncing}
    title={tr('title.professionalExperience')}
    onAction={handleSubmit}
    onClose={handleClose}
  >
    <FormComponent
      as={GroupInformation}
      companiesList={availableCompaniesDOM}
      isExisting={groupID}
      useInput={useInput}
    />
  </Dialog>
  );
}

GroupEditor.propTypes = {
  syncErrors: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  syncing: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSaveGroup: PropTypes.func.isRequired
};

export default GroupEditor;
